import { cn } from '@/lib/utils'

/**
 * Fixed pastel palette, deterministically chosen per person (hashed from
 * `seed`, e.g. a guest name or thread/reservation id) so the same person
 * always gets the same color across renders/reloads without persisting
 * anything - mirrors the artifact prototype's AVATAR_COLORS rotation.
 */
const AVATAR_COLORS = ['#ffb3c1', '#ffd580', '#9fd3c7', '#a7c7ff', '#c9b3ff', '#ffb38a']

function hashSeed(seed: unknown): number {
  let hash = 0
  const str = String(seed ?? '')
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash)
}

function getInitials(name?: string | null): string {
  const trimmed = (name || '').trim()
  if (!trimmed) return '?'
  const parts = trimmed.split(/\s+/)
  if (parts.length === 1) return parts[0][0].toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

const SIZE_CLASSES = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-11 w-11 text-base',
}

export interface AvatarProps {
  name?: string | null
  seed?: string | number | null
  size?: keyof typeof SIZE_CLASSES
  className?: string
}

/**
 * Colored circular avatar with initials - there is no real guest photo
 * source (Uplisting doesn't provide one), so this is always initials, never
 * a fabricated image.
 */
export function Avatar({ name, seed, size = 'md', className }: AvatarProps) {
  const backgroundColor = AVATAR_COLORS[hashSeed(seed ?? name) % AVATAR_COLORS.length]
  return (
    <div
      aria-hidden="true"
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full font-semibold text-charcoal',
        SIZE_CLASSES[size] || SIZE_CLASSES.md,
        className
      )}
      style={{ backgroundColor }}
    >
      {getInitials(name)}
    </div>
  )
}
